/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { alfrescoApi } from "@/AlfrescoApi";
import { newPristyError } from "@/services/ErrorService";

/**
 * Service for collaborative editing API calls.
 * Handles checking availability and initializing collaborative sessions.
 */
class CollabService {
  constructor() {
    this.basePath = "/api/collab";
  }

  /**
   * Gets the base URL for the Alfresco API
   *
   * @returns {string} Base URL
   */
  getBaseUrl() {
    return alfrescoApi.config.hostEcm;
  }

  /**
   * Gets the authentication ticket for WebSocket connection
   *
   * @returns {Promise<string>} Authentication ticket
   */
  async getTicket() {
    return alfrescoApi.getTicketEcm();
  }

  /**
   * Creates the Authorization header for API calls.
   * Uses Basic auth with ROLE_TICKET:ticket pattern (same as Alfresco webscripts).
   *
   * @param {string} ticket - The Alfresco ticket
   * @returns {string} Authorization header value
   */
  createAuthHeader(ticket) {
    return `Basic ${btoa(`ROLE_TICKET:${ticket}`)}`;
  }

  /**
   * Checks if collaborative editing is available for a node.
   *
   * @param {string} nodeId - The node ID to check
   * @returns {Promise<Object>} Availability info: { available, enabled, maxPeers, sessionId?, activePeers? }
   */
  async checkAvailability(nodeId) {
    try {
      const ticket = await this.getTicket();

      const response = await fetch(
        `${this.getBaseUrl()}/alfresco/s${this.basePath}/availability/${nodeId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: this.createAuthHeader(ticket),
          },
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          return { available: false, enabled: false, permissionLevel: "NONE" };
        }
        if (response.status === 403) {
          return {
            available: false,
            enabled: true,
            permissionLevel: "NONE",
            reason: "NO_READ_PERMISSION",
          };
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("[CollabService] Error checking availability:", error);
      throw newPristyError("collabService.checkAvailability", error);
    }
  }

  /**
   * Initializes a collaborative editing session.
   *
   * @param {string} nodeId - The node ID
   * @returns {Promise<Object>} Session info: { sessionId, version, activePeers }
   */
  async initSession(nodeId) {
    try {
      const ticket = await this.getTicket();

      const response = await fetch(
        `${this.getBaseUrl()}/alfresco/s${this.basePath}/session/init`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: this.createAuthHeader(ticket),
          },
          body: JSON.stringify({ nodeId }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("[CollabService] Error initializing session:", error);
      throw newPristyError("collabService.initSession", error);
    }
  }

  /**
   * Gets the WebSocket URL for collaborative editing.
   * Note: The URL does not contain the auth ticket for security reasons.
   * Authentication is done via a message after connection.
   *
   * @returns {string} WebSocket URL (without auth token)
   */
  getWebSocketUrl() {
    const baseUrl = this.getBaseUrl();

    // Convert http(s) to ws(s)
    const wsProtocol = baseUrl.startsWith("https") ? "wss" : "ws";
    const wsHost = baseUrl.replace(/^https?:\/\//, "");

    return `${wsProtocol}://${wsHost}/alfresco/collab`;
  }

  /**
   * Returns a client ID stable for the lifetime of the browser tab (window).
   *
   * Persisted in sessionStorage so that a reconnection or a page reload reuses the same ID:
   * the server can then recognize the rejoin and replace the stale peer instead of allocating a
   * new session slot. Without this, every reload leaked a slot and sessions filled up with ghost
   * peers (SESSION_FULL). sessionStorage is per-tab, so different tabs (or servers) still get
   * distinct IDs, allowing concurrent sessions.
   *
   * @returns {string} Client ID
   */
  generateClientId() {
    const STORAGE_KEY = "pristy.collab.clientId";
    const newId = () =>
      `client-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    try {
      let id = sessionStorage.getItem(STORAGE_KEY);
      if (!id) {
        id = newId();
        sessionStorage.setItem(STORAGE_KEY, id);
      }
      return id;
    } catch {
      // sessionStorage unavailable (private mode / restricted context) — fall back to a fresh ID
      return newId();
    }
  }
}

// Export singleton instance
const collabService = new CollabService();
export default collabService;
